import { StateProvider } from "@bryntum/core-thin";
import * as React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Auth0Config } from "~/config/auth0";
import { useAuth0 } from "~/config/auth0/use-auth0";
import { withAuthenticationRequired } from "~/config/auth0/with-authentication-required";
import { initNetworkStatus } from "~/config/capacitor/network/hook";
import { PushNotifications } from "~/config/capacitor/push-notifications/PushNotifications";
import { AppUrlListener } from "~/config/capacitor/router/router";
import { useTrackPage } from "~/config/segment/SegmentProvider";
import { captureException, SentryReact } from "~/config/sentry";
import { useGlobalOfflineQueue } from "~/config/stream-chat/useGlobalOfflineQueue";
import { UppyConfig } from "~/config/uppy/UppyConfig";
import { UrqlConfig } from "~/config/urql";
import { UserProvider } from "~/config/user/UserProvider";
import { SpinLoader } from "~/design-system/Spinner/SpinLoader";
import { UserPreferencesProvider } from "../../config/userPreference/UserPreferencesProvider";
import { AnonymousApp } from "./AnonymousApp";
import { AuthenticatedLinkRedirect } from "./AuthenticatedLinkRedirect";
import { AppVersionChecker } from "./components/AppVersionChecker";
import { ErrorFallback } from "./components/ErrorFallback/ErrorFallback";
import { FilesUploader } from "./components/FilesUploader/FilesUploader";
import { GoogleDriveCallback } from "./components/GoogleDriveCallback/GoogleDriveCallback";
import { Layout } from "./components/Layout/Layout";
import { NotFound } from "./components/NotFound/NotFound";
import { OfflineUploadsManager } from "./components/OfflineUploadsManager/OfflineUploadsManager";
import { RedirectAfterLogin } from "./components/RedirectAfterLogin/RedirectAfterLogin";
import { RedirectToMobileAppDialog } from "./components/RedirectMobileAppDialog/RedirectToMobileAppDialog";
import { TaskFilterProvider } from "./components/Tasks/TaskFilters/TaskFilterContext";
import { Toaster } from "./components/Toaster/Toaster";
import { UncachedDataOfflinePlaceholder } from "./components/UncachedDataOfflinePlaceholder/UncachedDataOfflinePlaceholder";
import { UpdateAppMetadataInfos } from "./components/UpdateAppMetadataInfos/UpdateAppMetadataInfos";
import { UpdateBrowserInfos } from "./components/UpdateBrowserInfos/UpdateBrowserInfos";
import { RightsScreen } from "./screens/AnonRightsScreen/RightsScreen";
import { UnavailableScreen } from "./screens/UnavailableScreen/UnavailableScreen";
import { UserNotificationsScreen } from "./screens/User/screens/UserNotificationsScreen/UserNotificationsScreen";
import { useRedirectAfterLogin } from "./utils/useRedirectAfterLogin/useRedirectAfterLogin";
// Initialize network status monitoring
void initNetworkStatus();

// Contain the basic routes of our app
// Will be re-used in Layout to retrieve current params
// If you add more root routes to the app and want them to
// be accessible into the mobile app via deeplinks, you must
// add thems to the mobile build config as well
// see docs here: https://app.gitbook.com/o/-LlhZY09V4VTR7cy_55t/s/-MSitZZVNivUi5eDfPNe/guidelines/devices/mobile/how-to-add-new-deeplink-path-to-the-app
const APP_BASE_ROUTES_PATTERNS = {
  dashboard: "/dashboard/*",
  google_drive_callback: "/google-drive/callback",
  onboarding: "/onboarding",
  org: "/org/:orgId/*",
  project: "/project/:projectId/*",
  rights: "/rights/*",
  unavailable: "/unavailable",
  user_notifications: "/user/notifications",
  user_offline: "/user/offline/*",
  user_profile: "/user/profile",
};

const Dashboard = React.lazy(() =>
  import("./screens/Dashboard/Dashboard")
    .then((m) => ({
      default: m.Dashboard,
    }))
    .catch(() => {
      return { default: () => <UncachedDataOfflinePlaceholder /> };
    })
);

const Org = React.lazy(() =>
  import("./screens/Org/Org").then((m) => ({
    default: m.Org,
  }))
);

const Project = React.lazy(() =>
  import("./screens/Project/Project").then((m) => ({
    default: m.Project,
  }))
);

const UserProfileScreen = React.lazy(() =>
  import("./screens/User/screens/UserProfileScreen/UserProfileScreen").then(
    (m) => ({
      default: m.UserProfileScreen,
    })
  )
);

const UserOfflineFiles = React.lazy(() =>
  import("./screens/User/screens/UserOfflineFiles").then((m) => ({
    default: m.UserOfflineFiles,
  }))
);

const SelfOnboardingScreen = React.lazy(() =>
  import("./screens/SelfOnboardingScreen/SelfOnboardingScreen").then((m) => ({
    default: m.SelfOnboardingScreen,
  }))
);

const AuthenticatedApp = withAuthenticationRequired(
  function AuthenticatedApp() {
    useGlobalOfflineQueue();

    return (
      <SentryReact.ErrorBoundary
        fallback={ErrorFallback}
        beforeCapture={(scope) => {
          scope.setTag("ScreenError", "AuthenticatedApp");
        }}
      >
        <UserProvider>
          <UserPreferencesProvider>
            <RedirectAfterLogin>
              <PushNotifications>
                <UpdateBrowserInfos>
                  <UpdateAppMetadataInfos>
                    <UppyConfig>
                      <TaskFilterProvider projectFilters={{}}>
                        <Layout>
                          <SentryReact.ErrorBoundary
                            fallback={ErrorFallback}
                            beforeCapture={(scope) => {
                              scope.setTag("ScreenError", "LayoutError");
                            }}
                          >
                            <React.Suspense fallback={<SpinLoader />}>
                              <Routes>
                                <Route
                                  path="/"
                                  element={<Navigate to="/dashboard" replace />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.dashboard}
                                  element={<Dashboard />}
                                />
                                <Route
                                  path={
                                    APP_BASE_ROUTES_PATTERNS.google_drive_callback
                                  }
                                  element={<GoogleDriveCallback />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.org}
                                  element={<Org />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.project}
                                  element={<Project />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.user_profile}
                                  element={<UserProfileScreen />}
                                />
                                <Route
                                  path={
                                    APP_BASE_ROUTES_PATTERNS.user_notifications
                                  }
                                  element={<UserNotificationsScreen />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.user_offline}
                                  element={<UserOfflineFiles />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.unavailable}
                                  element={<UnavailableScreen />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.onboarding}
                                  element={<SelfOnboardingScreen />}
                                />
                                <Route
                                  path={APP_BASE_ROUTES_PATTERNS.rights}
                                  element={<RightsScreen />}
                                />
                                <Route path="*" element={<NotFound />} />
                              </Routes>
                            </React.Suspense>
                            <RedirectToMobileAppDialog />
                          </SentryReact.ErrorBoundary>
                        </Layout>
                      </TaskFilterProvider>
                      <Toaster />
                      <FilesUploader />
                      <OfflineUploadsManager />
                    </UppyConfig>
                  </UpdateAppMetadataInfos>
                </UpdateBrowserInfos>
              </PushNotifications>
            </RedirectAfterLogin>
          </UserPreferencesProvider>
        </UserProvider>
      </SentryReact.ErrorBoundary>
    );
  }
);

// Intermediary app that logs Auth0 errors and delegates auth gating to
// withAuthenticationRequired (which owns the circuit breaker, retry button
// and auto-redirect logic).  We intentionally do NOT block AuthenticatedApp
// rendering on error — the circuit breaker in authFlowController prevents
// infinite loops, and keeping withAuthenticationRequired mounted ensures
// the user always has a functional retry path.
const Auth0ProtectedApp = () => {
  void useTrackPage();
  const { error } = useAuth0();

  const { saveCurrentURL } = useRedirectAfterLogin();

  React.useEffect(() => {
    // We register the current URL to retrieve it after Auth0 login
    saveCurrentURL();
  }, [saveCurrentURL]);

  React.useEffect(() => {
    if (!error) {
      return;
    }

    captureException({
      exception: JSON.stringify(error),
      hint: "🔐🔴 Auth0ProtectedApp error",
    });
  }, [error]);

  StateProvider.setup("local");

  return <AuthenticatedApp />;
};

const PublicApp = () => {
  return (
    <BrowserRouter>
      <AppUrlListener>
        <AppVersionChecker>
          <Auth0Config>
            <UrqlConfig>
              <Routes>
                <Route path="/rights/*" element={<RightsScreen />} />
                {/* IMPORTANT! Make sure to change the auth0 rule for /register/userinfos if you change me */}
                <Route path="/anon/*" element={<AnonymousApp />} />
                {/* alink stand for 'AuthenticatedLink' */}
                <Route path="/alink" element={<AuthenticatedLinkRedirect />} />
                <Route path="*" element={<Auth0ProtectedApp />} />
              </Routes>
            </UrqlConfig>
          </Auth0Config>
        </AppVersionChecker>
      </AppUrlListener>
    </BrowserRouter>
  );
};

const SentryApp = SentryReact.withErrorBoundary(PublicApp, {
  beforeCapture: (scope) => {
    scope.setTag("ScreenError", "PublicApp");
  },
  fallback: ErrorFallback,
});

export { SentryApp as App, APP_BASE_ROUTES_PATTERNS, ErrorFallback };
